/**
 * src/plugins/commerce/garage-shop-contracts.ts
 *
 * Creator Garage Commerce Contracts & Data Structures (Step 4.2).
 */

export type ProductFulfillmentType =
  | 'DIGITAL_DOWNLOAD'
  | 'PHYSICAL_SHIPMENT'
  | 'SERVICE_CONSULTATION';

import type { MediaFeedItem } from '../../domain/media/media-feed-contracts.js';

export interface GarageListing {
  listingId: string;
  creatorDid: string;
  channelId: string;
  title: string;
  description: string;
  fulfillmentType: ProductFulfillmentType;
  priceUsd: number;
  priceTokens: number;
  stockRemaining: number;
  imageUrl?: string;
  location?: string;
  sellerDisplayName?: string;
  sellerRating?: number;
  reviewsCount?: number;
  digitalPayloadCid?: string;     // IPFS/Edge Vault CID
  isPinnedToLiveStream?: boolean; // Active stream overlay anchor
  createdAt: number;
  mediaPayload?: MediaFeedItem;
  demoVideoUrl?: string;
  specPdfUrl?: string;
}

export type EscrowOrderStatus =
  | 'FUNDS_LOCKED'
  | 'FULFILLMENT_DECLARED'
  | 'FUNDS_RELEASED'
  | 'DISPUTED';

export interface EscrowOrder {
  orderId: string;
  listingId: string;
  buyerDid: string;
  sellerDid: string;
  priceTokens: number;
  priceUsd: number;
  status: EscrowOrderStatus;
  lockedAt: number;
  autoReleaseDeadline: number; // 14-day timeout
  fulfillmentDetails?: {
    trackingNumber?: string;
    carrier?: string;
    digitalPayloadCid?: string;
    decryptionKey?: string;
    consultationNotes?: string;
    declaredAt: number;
  };
  releasedAt?: number;
  disputedAt?: number;
  disputeReason?: string;
  juryIncidentId?: string;
  protocolFeeTokens: number;
  sellerNetTokens: number;
}

export interface VouchedReview {
  reviewId: string;
  orderId: string;
  listingId: string;
  sellerDid: string;
  reviewerDid: string;
  rating: number; // 1 to 5
  comment: string;
  reviewerTrustTier: number;
  createdAt: number;
  isSybilFlagged?: boolean;
}

export const INITIAL_GARAGE_DROPS: GarageListing[] = [
  {
    listingId: 'drop_lofi_synth_pack',
    creatorDid: 'did:key:sound_artisan_01',
    channelId: 'chan_art_gallery',
    title: 'Analog Lo-Fi Synth Sample Pack',
    description: '120 royalty-free reel-to-reel stem loops. Mastered on analog tape with vintage preamps.',
    fulfillmentType: 'DIGITAL_DOWNLOAD',
    priceUsd: 25,
    priceTokens: 250,
    stockRemaining: 999,
    imageUrl: 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=600&auto=format&fit=crop&q=80',
    location: 'Kuala Lumpur, MY',
    sellerDisplayName: 'AudioCraft Labs',
    sellerRating: 4.9,
    reviewsCount: 88,
    isPinnedToLiveStream: true,
    createdAt: Date.now(),
  },
  {
    listingId: 'drop_esp32_bridge_enclosure',
    creatorDid: 'did:key:mesh_hardware_fab',
    channelId: 'chan_tasks',
    title: 'ESP32-S3 Snap-Fit Bridge Enclosure',
    description: 'Custom 3D-printed enclosure with SMA antenna cutout, IP65 seal, and battery retention clip.',
    fulfillmentType: 'PHYSICAL_SHIPMENT',
    priceUsd: 1.5,
    priceTokens: 15,
    stockRemaining: 8,
    imageUrl: 'https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=600&auto=format&fit=crop&q=80',
    location: 'Petaling Jaya, MY',
    sellerDisplayName: 'Mesh Fab Works',
    sellerRating: 5.0,
    reviewsCount: 24,
    isPinnedToLiveStream: true,
    createdAt: Date.now(),
  },
  {
    listingId: 'drop_depin_consulting_slot',
    creatorDid: 'did:key:core_arch_contributor',
    channelId: 'chan_general',
    title: '1-on-1 DePIN Architecture Consult',
    description: 'Private 60-minute technical deep-dive on silicon-native mesh protocols and token economics.',
    fulfillmentType: 'SERVICE_CONSULTATION',
    priceUsd: 5.0,
    priceTokens: 50,
    stockRemaining: 2,
    imageUrl: 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=600&auto=format&fit=crop&q=80',
    location: 'Remote • Singapore, SG',
    sellerDisplayName: 'Core Architecture Guild',
    sellerRating: 5.0,
    reviewsCount: 15,
    isPinnedToLiveStream: true,
    createdAt: Date.now(),
  },
  {
    listingId: 'drop_micro_lot_roasted_coffee',
    creatorDid: 'did:key:bangsar_roasters',
    channelId: 'chan_general',
    title: 'Artisan Micro-Lot Roasted Beans (250g)',
    description: 'Direct-trade Ethiopian Yirgacheffe roasted weekly in Bangsar. Peach, jasmine, and citrus notes.',
    fulfillmentType: 'PHYSICAL_SHIPMENT',
    priceUsd: 18,
    priceTokens: 180,
    stockRemaining: 45,
    imageUrl: 'https://images.unsplash.com/photo-1559056199-641a0ac8b55e?w=600&auto=format&fit=crop&q=80',
    location: 'Bangsar, Kuala Lumpur',
    sellerDisplayName: 'Bangsar Roasters',
    sellerRating: 4.9,
    reviewsCount: 112,
    isPinnedToLiveStream: false,
    createdAt: Date.now(),
  },
  {
    listingId: 'drop_yubikey_5c_nfc',
    creatorDid: 'did:key:sec_hardware_dist',
    channelId: 'chan_tasks',
    title: 'YubiKey 5C NFC Security Key',
    description: 'FIDO2 / WebAuthn hardware authenticator with dual USB-C and NFC touch validation.',
    fulfillmentType: 'PHYSICAL_SHIPMENT',
    priceUsd: 55,
    priceTokens: 550,
    stockRemaining: 14,
    imageUrl: 'https://images.unsplash.com/photo-1614064641938-3bbee52942c7?w=600&auto=format&fit=crop&q=80',
    location: 'Subang Jaya, MY',
    sellerDisplayName: 'Hardware Crypt Store',
    sellerRating: 5.0,
    reviewsCount: 64,
    isPinnedToLiveStream: false,
    createdAt: Date.now(),
  },
  {
    listingId: 'drop_lora_antenna_outdoor',
    creatorDid: 'did:key:rf_engineer_my',
    channelId: 'chan_node_runners',
    title: 'LoRa 915MHz High-Gain Fiberglass Antenna',
    description: '5.8 dBi omni-directional outdoor antenna for long-range packet relay and DePIN coverage.',
    fulfillmentType: 'PHYSICAL_SHIPMENT',
    priceUsd: 32,
    priceTokens: 320,
    stockRemaining: 19,
    imageUrl: 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=600&auto=format&fit=crop&q=80',
    location: 'Johor Bahru, MY',
    sellerDisplayName: 'Mesh RF Works',
    sellerRating: 4.9,
    reviewsCount: 37,
    isPinnedToLiveStream: false,
    createdAt: Date.now(),
  },
  {
    listingId: 'drop_solar_mesh_node_kit',
    creatorDid: 'did:key:offgrid_sol_labs',
    channelId: 'chan_node_runners',
    title: 'Portable Solar Mesh Node 20W Kit',
    description: 'Folding monocrystalline solar panel, MPPT charge regulator, and weatherproof enclosure kit.',
    fulfillmentType: 'PHYSICAL_SHIPMENT',
    priceUsd: 75,
    priceTokens: 750,
    stockRemaining: 6,
    imageUrl: 'https://images.unsplash.com/photo-1509391365360-2e959784a276?w=600&auto=format&fit=crop&q=80',
    location: 'Kuala Lumpur, MY',
    sellerDisplayName: 'Off-Grid DePIN Tech',
    sellerRating: 5.0,
    reviewsCount: 53,
    isPinnedToLiveStream: false,
    createdAt: Date.now(),
  },
];

export const DEFAULT_GARAGE_LISTINGS = INITIAL_GARAGE_DROPS;
